/**
 * Moto Manufacturer Hooks
 * =======================
 * React Query hooks for motorcycle manufacturer operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motoManufacturerApi } from '@/services/api'
import type { MotoManufacturerCreate, MotoManufacturerUpdate } from '@/types'

// Query keys
export const motoManufacturerKeys = {
  all: ['motoManufacturers'] as const,
  lists: () => [...motoManufacturerKeys.all, 'list'] as const,
  list: () => [...motoManufacturerKeys.lists()] as const,
  details: () => [...motoManufacturerKeys.all, 'detail'] as const,
  detail: (id: number) => [...motoManufacturerKeys.details(), id] as const,
}

/**
 * Hook to fetch all moto manufacturers.
 */
export function useMotoManufacturers() {
  return useQuery({
    queryKey: motoManufacturerKeys.list(),
    queryFn: () => motoManufacturerApi.list(),
  })
}

/**
 * Hook to fetch a single moto manufacturer.
 */
export function useMotoManufacturer(id: number) {
  return useQuery({
    queryKey: motoManufacturerKeys.detail(id),
    queryFn: () => motoManufacturerApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new moto manufacturer.
 */
export function useCreateMotoManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: MotoManufacturerCreate) => motoManufacturerApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: motoManufacturerKeys.lists() })
    },
  })
}

/**
 * Hook to update a moto manufacturer.
 */
export function useUpdateMotoManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MotoManufacturerUpdate }) =>
      motoManufacturerApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: motoManufacturerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: motoManufacturerKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete a moto manufacturer.
 */
export function useDeleteMotoManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => motoManufacturerApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: motoManufacturerKeys.lists() })
    },
  })
}
